import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Portal } from '@hcc/types';
import { api, ApiError } from '../lib/api';
import { disconnectSocket } from '../lib/socket';

export interface SessionUser {
  id: string;
  email: string;
  display_name: string;
  roles: string[];
  /** Set when this auth identity is linked to an attendee (people) row. */
  person_id: string | null;
  /** Active mobilisation. Null = no scope (all mobilisations). */
  current_mobilisation_id?: string | null;
  login_method: 'oidc' | 'dev' | 'emergency' | 'otp';
  login_at: string;
}

interface MeResponse {
  user: SessionUser;
  privacy_accepted?: boolean;
  privacy_policy_url?: string | null;
}

const ME_KEY = ['auth', 'me'] as const;
const PRIVACY_KEY = ['auth', 'privacy'] as const;

export interface PrivacyStatus {
  accepted: boolean;
  policy_url: string | null;
}

/**
 * Hook returning the current logged-in user, or null if not signed in.
 * Driven by the /api/auth/me probe; cached and reused across components.
 */
export function useCurrentUser() {
  return useQuery({
    queryKey: ME_KEY,
    queryFn: async (): Promise<SessionUser | null> => {
      try {
        const { user } = await api<MeResponse>('/api/auth/me');
        return user;
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

export interface AuthMethods {
  dev_login_active: boolean;
  oidc_configured: boolean;
  emergency_login_enabled: boolean;
  otp_login_enabled: boolean;
  /** Which portal this host serves: 'main' (default) or 'training'. */
  portal: Portal;
}

const METHODS_KEY = ['auth', 'methods'] as const;

/**
 * Which login flows the API currently offers. Driven by env on the
 * server. Used by the Login page so dev-only UI doesn't leak into
 * production builds.
 */
export function useAuthMethods() {
  return useQuery({
    queryKey: METHODS_KEY,
    queryFn: () => api<AuthMethods>('/api/auth/methods'),
    staleTime: 5 * 60_000,
  });
}

/**
 * Which portal this host serves, derived server-side from the hostname and
 * exposed on the public /methods probe so it is known before login. Defaults
 * to 'main' while the probe is in flight (or if it fails) so the standard app
 * is what renders when in doubt. The whole route tree branches on this.
 */
export function usePortal(): Portal {
  const { data } = useAuthMethods();
  return data?.portal ?? 'main';
}

export function useDevLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      return api<{ user: SessionUser }>('/api/auth/dev/login', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    onSuccess: ({ user }) => {
      qc.setQueryData(ME_KEY, user);
    },
  });
}

/**
 * Request a one-time login code to a personal email. Always resolves
 * (the server returns 200 regardless of whether the address is known)
 * so the UI never reveals whether an email is on file.
 */
export function useRequestOtp() {
  return useMutation({
    mutationFn: async (input: { email: string }) =>
      api<{ ok: true }>('/api/auth/otp/request', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
  });
}

/** Verify a code and start a session. */
export function useVerifyOtp() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; code: string }) =>
      api<{ user: SessionUser }>('/api/auth/otp/verify', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: ({ user }) => {
      qc.setQueryData(ME_KEY, user);
    },
  });
}

export function useEmergencyLogin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { email: string; password: string }) => {
      return api<{ user: SessionUser }>('/api/auth/emergency-login', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    },
    onSuccess: ({ user }) => {
      qc.setQueryData(ME_KEY, user);
    },
  });
}

/**
 * Privacy-notice acceptance state from /api/auth/me. Separate hook
 * so the modal that gates first login doesn't need to round-trip
 * through the SessionUser shape.
 */
export function usePrivacyStatus() {
  return useQuery({
    queryKey: PRIVACY_KEY,
    queryFn: async (): Promise<PrivacyStatus | null> => {
      try {
        const r = await api<MeResponse>('/api/auth/me');
        return {
          accepted: !!r.privacy_accepted,
          policy_url: r.privacy_policy_url ?? null,
        };
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) {
          return null;
        }
        throw err;
      }
    },
    staleTime: 60_000,
  });
}

export function useAcceptPrivacy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      return api<{ privacy_accepted: boolean }>(
        '/api/auth/accept-privacy',
        { method: 'POST', body: JSON.stringify({}) },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: PRIVACY_KEY });
    },
  });
}

export function useLogout() {
  return useMutation({
    mutationFn: async () => {
      await api<void>('/api/auth/logout', { method: 'POST' });
    },
    onSuccess: () => {
      // Socket auth and room membership are evaluated once per connection, so
      // without this a shared station tablet keeps receiving the old session's
      // events after logout, and the next user inherits them. Tear the
      // singleton down (the next login reconnects under the new session).
      disconnectSocket();
      // Hard navigation, not a client-side redirect. Clearing the query cache
      // and nulling ME left the current protected page rendered until a manual
      // refresh (the route guard did not re-run), so navigate for real: this
      // tears down all React / query state and boots fresh on the sign-in page.
      // Both portals serve /login.
      window.location.assign('/login');
    },
  });
}
